import React from 'react';
import { Card, Space, Button, Typography, Tag, Avatar, Row, Col, Divider } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, PhoneOutlined, MoreOutlined, MessageOutlined } from '@ant-design/icons';
import { useScheduledDateRender, useUserTimestampRender } from '@src/hooks';
import { useEngagementModeRender } from './useEngagementModeRender';
import { useStatusRender } from './useStatusRender';
import { useSourceRender } from './useSourceRender';

const { Text } = Typography;

interface LeadCardViewProps {
  dataSource: any[];
  onEditClick?: (record: any) => void;
  onDeleteClick?: (record: any) => void;
  onViewClick?: (record: any) => void;
  onRowClick?: (record: any) => void;
  showEdit?: boolean;
  showDelete?: boolean;
  showView?: boolean;
  customActions?: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: (record: any) => void;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    tooltip?: string;
    confirmMessage?: string;
  }>;
  rowKey?: string;
}

export const useLeadCardView = () => {
  const renderFollowUpDate = useScheduledDateRender();
  const getEngagementModeConfig = useEngagementModeRender();
  const getStatusConfig = useStatusRender();
  const getSourceConfig = useSourceRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });
  
  const LeadCardView: React.FC<LeadCardViewProps> = ({
    dataSource,
    onEditClick,
    onDeleteClick,
    onViewClick,
    onRowClick,
    showEdit = true,
    showDelete = true,
    showView = false,
    customActions = [],
    rowKey = 'id',
  }) => {
    // Helper function to render field value
    const renderFieldValue = (value: any, type: string) => {
      if (value === null || value === undefined || value === '') {
        return '-';
      }

      switch (type) {
        case 'phone':
          return (
            <Space size="small">
              <PhoneOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
              <Text copyable style={{ fontSize: '12px' }}>{value}</Text>
            </Space>
          );
        case 'status':
          const getStatusColor = (status: string) => {
            const lowerStatus = status.toLowerCase();
            if (lowerStatus.includes('new') || lowerStatus.includes('active')) return 'blue';
            if (lowerStatus.includes('completed') || lowerStatus.includes('success') || lowerStatus.includes('converted')) return 'green';
            if (lowerStatus.includes('pending') || lowerStatus.includes('waiting')) return 'orange';
            if (lowerStatus.includes('cancelled') || lowerStatus.includes('failed')) return 'red';
            if (lowerStatus.includes('clinic')) return 'purple';
            return 'default';
          };

          return (
            <Tag color={getStatusColor(value)} style={{ fontSize: '11px', padding: '2px 8px' }}>
              {value}
            </Tag>
          );
        case 'date':
          return (
            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 500 }}>
              {value}
            </Text>
          );
        case 'engagement':
        case 'source':
          return (
            <Text style={{ fontSize: '12px' }}>{value}</Text>
          );
        default:
          return <Text style={{ fontSize: '12px' }}>{value}</Text>;
      }
    };

    // Helper function to render actions
    const renderActions = (record: any) => {
      const actions = [];

      if (showView) {
        actions.push(
          <Button
            key="view"
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onViewClick?.(record);
            }}
            style={{ color: '#1890ff' }}
          />
        );
      }

      if (showEdit) {
        actions.push(
          <Button
            key="edit"
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick?.(record);
            }}
            style={{ color: '#faad14' }}
          />
        );
      }

      if (showDelete) {
        actions.push(
          <Button
            key="delete"
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick?.(record);
            }}
            style={{ color: '#ff4d4f' }}
          />
        );
      }

      // Add custom actions
      customActions.forEach((action) => {
        actions.push(
          <Button
            key={action.key}
            type="text"
            size="small"
            icon={action.icon}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(record);
            }}
            style={{ 
              color: action.danger ? '#ff4d4f' : action.type === 'primary' ? '#1890ff' : '#666'
            }}
          />
        );
      });

      return actions;
    };


    return (
      <div className="p-0">
        {dataSource && dataSource.length > 0 ? dataSource.map((record, index) => {
          const name = record.patient_name || `Lead ${record[rowKey]}`;
          const phone = record.patient_mobile_no || '';
          const status = record.status || '';
          const engagement = record.engagement_mode || '';
          const source = record.source || '';
          const followUp = record.follow_up_date || '';
          const user = record.created_by_name || '';

          return (
            <Card
              key={record[rowKey] || index}
              size="small"
              className={`
                mb-4 mx-4 
                ${index === 0 ? 'mt-4' : 'mt-0'}
                rounded-xl shadow-sm border border-gray-200 
                ${onRowClick ? 'cursor-pointer' : 'cursor-default'}
                transition-all duration-200 ease-in-out bg-white
              `}
              bodyStyle={{ padding: '16px' }}
              onClick={() => onRowClick?.(record)}
            >
              {/* Top Section - ID, Name, Phone, Status */}
              <Row gutter={[12, 8]} align="middle" className="mb-3">
                <Col flex="auto">
                  <Space size="small">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Text className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          #{record[rowKey]}
                        </Text>
                        <div className="flex flex-col">
                            <div className="text-base font-bold text-black leading-tight pl-2">
                            {name}
                            </div>
                            {phone && (
                                <div className="text-xs text-gray-600 mt-0.5">
                                +{phone}
                                </div>
                            )}
                        </div>
                      </div>  
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Space size="small">
                    {status && getStatusConfig(status)}
                  </Space>
                </Col>
              </Row>

              {/* Middle Section - Engagement and Source */}
              {(engagement || source) && (
                <Row gutter={[16, 8]} className="mb-3">
                  {engagement && (
                    <Col span={12}>
                      <div>
                        <Text type="secondary" className="text-xs">
                          Engagement Mode
                        </Text>
                        <div className="text-xs font-medium mt-0.5">
                          {getEngagementModeConfig(engagement)}
                        </div>
                      </div>
                    </Col>
                  )}
                  {source && (
                    <Col span={12}>
                      <div>
                        <Text type="secondary" className="text-xs">
                          Source
                        </Text>
                        <div className="text-xs font-medium mt-0.5">
                          {getSourceConfig(source)}
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              )}

              {/* Bottom Section - Follow Up and Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <div className="flex items-center gap-2">
                  {followUp && (
                    <div>
                      <Text type="secondary" className="text-xs">
                        Follow Up
                      </Text>
                      <div className="text-xs font-medium mt-0.5">
                        {renderFollowUpDate(followUp, status)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {userTimestampRender(record)}
                  {renderActions(record)}
                </div>
              </div>
            </Card>
          );
        }) : (
          <div className="py-10 px-5 text-center text-gray-500 text-sm">
            No leads found
          </div>
        )}
      </div>
    );
  };

  return { LeadCardView };
};
